import { applyDiff } from '../collab/diffEngine';
import { pushCapped, withUndoApply, isPreviewMode } from '../undo/undoHistory';
import { flushUndoCapture } from '../undo/undoCapture';

// Slice du do/undo. Les piles vivent dans le store pour que l'UI (boutons,
// disabled) s'abonne à leur taille. Elles ne sont jamais diffusées (hors
// whitelist diffEngine) ni persistées : historique local, en mémoire, perdu au
// reload.
//
// Application : on mute le store derrière withUndoApply → undoCapture ignore la
// mutation (pas de re-capture), mais structureSync la diffuse et la persiste
// comme une édition normale (un undo gagne ; conflits collab gérés plus tard).
// undo()/redo() retournent le `focus` de l'entrée appliquée — la navigation
// (potentiellement cross-mode) est faite par la couche UI (cf. useUndoRedo).
export const undoActions = (set, get) => ({
  // Empile une entrée de capture et invalide le redo (toute nouvelle édition
  // rend le redo caduc).
  pushUndoEntry: (entry) =>
    set(
      (state) => ({
        undoStack: pushCapped(state.undoStack, entry),
        redoStack: [],
      }),
      false,
      'pushUndoEntry'
    ),

  clearUndoHistory: () =>
    set({ undoStack: [], redoStack: [] }, false, 'clearUndoHistory'),

  undo: () => {
    // Interdit pendant l'aperçu PDF (on n'y édite pas le document).
    if (isPreviewMode(get())) return null;
    // Commit d'abord l'édition en cours (debounce en attente) pour qu'elle
    // devienne l'entrée du sommet, puis on l'annule.
    flushUndoCapture();

    const { undoStack } = get();
    if (undoStack.length === 0) return null;
    const [entry, ...rest] = undoStack;

    withUndoApply(() => {
      set(
        {
          ...applyDiff(get(), entry.inverse),
          undoStack: rest,
          redoStack: pushCapped(get().redoStack, entry),
        },
        false,
        'undo'
      );
    });
    return entry.focus;
  },

  redo: () => {
    if (isPreviewMode(get())) return null;

    const { redoStack } = get();
    if (redoStack.length === 0) return null;
    const [entry, ...rest] = redoStack;

    withUndoApply(() => {
      set(
        {
          ...applyDiff(get(), entry.forward),
          redoStack: rest,
          undoStack: pushCapped(get().undoStack, entry),
        },
        false,
        'redo'
      );
    });
    return entry.focus;
  },
});
